import React, { useRef, useEffect, useState } from 'react';
import { privacyPolicyData } from '../data/privacyPolicy';
import type { PrivacyPolicySection } from '../types/privacy-policy';
import SectionHead from './SectionHead';
import layout from '../styles/layout.module.css';
import styles from './PrivacySection.module.css';

/**
 * プライバシーポリシーセクションコンポーネント
 * 個人情報保護法に準拠したプライバシーポリシーを表示
 */
const PrivacySection: React.FC = () => {
  const mainContentRef = useRef<HTMLDivElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  /**
   * メインコンテンツにスキップする関数
   */
  const skipToMainContent = (): void => {
    if (mainContentRef.current) {
      mainContentRef.current.focus();
      mainContentRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  /**
   * キーボードイベントハンドラー
   */
  const handleKeyDown = (event: React.KeyboardEvent): void => {
    // Escキーでページトップに戻る
    if (event.key === 'Escape') {
      const pageTitle = document.getElementById('privacy-title');
      if (pageTitle) {
        pageTitle.focus();
        pageTitle.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  /**
   * コンポーネントマウント時にフォーカス管理とアニメーションを設定
   */
  useEffect(() => {
    // スタガードリビールアニメーションを開始
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 100);

    // URLハッシュが#privacyの場合、ページタイトルにフォーカス
    if (window.location.hash === '#privacy') {
      const pageTitle = document.getElementById('privacy-title');
      if (pageTitle) {
        setTimeout(() => {
          pageTitle.focus();
        }, 100);
      }
    }

    return () => clearTimeout(timer);
  }, []);
  /**
   * セクションコンテンツをレンダリング
   * 文字列配列の場合はリスト形式で表示
   */
  const renderContent = (content: string | string[]): React.ReactNode => {
    if (Array.isArray(content)) {
      return (
        <ul className={styles.contentList}>
          {content.map((item, index) => (
            <li key={index} className={styles.contentListItem}>
              {item}
            </li>
          ))}
        </ul>
      );
    }
    return <p className={styles.contentText}>{content}</p>;
  };

  /**
   * サブセクションをレンダリング
   */
  const renderSubsections = (subsections: PrivacyPolicySection[]): React.ReactNode => {
    return subsections.map((subsection) => (
      <div 
        key={subsection.id} 
        className={styles.subsection}
        role="region"
        aria-labelledby={`subsection-${subsection.id}`}
      >
        <h3 
          id={`subsection-${subsection.id}`} 
          className={styles.subsectionTitle}
          aria-level={3}
        >
          {subsection.title}
        </h3>
        {renderContent(subsection.content)}
        {subsection.subsections && renderSubsections(subsection.subsections)}
      </div>
    ));
  };

  /**
   * メインセクションをレンダリング
   */
  const renderSection = (section: PrivacyPolicySection): React.ReactNode => (
    <section 
      key={section.id} 
      className={styles.policySection} 
      aria-labelledby={`section-${section.id}`}
      role="region"
    >
      <h2 
        id={`section-${section.id}`} 
        className={styles.sectionTitle}
        aria-level={2}
      >
        {section.title}
      </h2>
      {renderContent(section.content)}
      {section.subsections && renderSubsections(section.subsections)}
    </section>
  );

  return (
    <section
      id="privacy"
      className={styles.privacySection}
      role="region"
      aria-labelledby="privacy-title"
      aria-describedby="privacy-intro"
      onKeyDown={handleKeyDown}
    >
      {/* スキップリンク */}
      <a 
        href="#privacy-main-content"
        className={styles.skipLink}
        onClick={(e) => {
          e.preventDefault();
          skipToMainContent();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            skipToMainContent();
          }
        }}
      >
        メインコンテンツにスキップ
      </a>

      <div
        className={`${layout.wrap} ${layout.sectionInner} ${styles.inner} ${isLoaded ? styles.loaded : ''}`}
      >
        <SectionHead
          eyebrow="PRIVACY POLICY"
          title="プライバシーポリシー"
          titleId="privacy-title"
          headingLevel={1}
          titleAriaLabel="プライバシーポリシー - メインタイトル"
          lead={
            <div
              className={styles.policyDates}
              role="contentinfo"
              aria-label="ポリシー制定・更新日"
            >
              <p className={styles.dateInfo}>
                制定日:{' '}
                <time dateTime={privacyPolicyData.establishedDate}>
                  {new Date(privacyPolicyData.establishedDate).toLocaleDateString('ja-JP', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </time>
              </p>
              <p className={styles.dateInfo}>
                最終更新日:{' '}
                <time dateTime={privacyPolicyData.lastUpdated}>
                  {new Date(privacyPolicyData.lastUpdated).toLocaleDateString('ja-JP', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </time>
              </p>
            </div>
          }
        />
        <div className={layout.columns}>

        <div 
          id="privacy-main-content"
          ref={mainContentRef}
          className={`${layout.contentColumn} ${styles.policyContent}`}
          aria-label="プライバシーポリシーのメインコンテンツ"
        >
          <div 
            className={styles.policyIntro}
            role="note"
            aria-label="プライバシーポリシーの概要"
          >
            <p id="privacy-intro" className={styles.introText}>
              株式会社eNEW Studioは、お客様の個人情報の保護を重要な責務と考え、
              個人情報保護法その他の関連法令を遵守し、適切な取り扱いに努めます。
            </p>
          </div>

          <div 
            className={styles.policySections}
            role="group"
            aria-label="プライバシーポリシーの詳細セクション"
          >
            {privacyPolicyData.sections.map(renderSection)}
          </div>
        </div>
        </div>
      </div>
    </section>
  );
};

export default PrivacySection;